<template>
	<div>
		<v-page-outlets-disclosure @open="onOpen()" @close="onClose()">
			<template #button>
				<div class="flex w-full flex-row items-start last:justify-end">
					<div class="flex w-full flex-col">
						<div class="flex w-full flex-row items-center justify-between">
							<p>{{ outlet.name }}</p>
						</div>

						<client-only>
							<v-page-outlets-opening-hours :outlet="outlet" />
						</client-only>
					</div>

					<div
						class="flex min-h-5 flex-1 flex-row items-center justify-end space-x-sm"
					>
						<p
							v-show="showDistance && locationCoords !== undefined"
							class="w-20 text-right text-xs text-gray-400"
						>
							{{ distanceText }} km
						</p>

						<span v-show="hasTrailerRental" :title="t('trailer-rental')">
							<v-icon icon="trailer" />
						</span>

						<span v-show="hasWarehousePickup" :title="t('warehouse-pickup')">
							<v-icon icon="warehouse" />
						</span>
					</div>
				</div>
			</template>

			<template #content>
				<v-page-outlets-disclosure-content :outlet="outlet" />
			</template>
		</v-page-outlets-disclosure>
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";
import type {Coordinates} from "@pkg/shared/types";

import calculateDistance from "@pkg/shared/utils/calculate-distance";

const properties = defineProps<{
	outlet: ISbStoryData; // Outlet data
	locationCoords?: Coordinates;
	showDistance?: boolean; // Check if distance should be shown
}>();

const emit = defineEmits<{
	(event: "opened"): void;
	(event: "closed"): void;
}>();

const {$i18n} = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);
const {t} = useI18n();

// Emits the location to parent when disclosure is opened.
function onOpen() {
	emit("opened");
}

// Resets location when disclosure is closed and emits closing.
function onClose() {
	emit("closed");
}

const hasTrailerRental = computed(
	() => properties.outlet.content?.trailer_rental && currentLang.value === "fi",
);

const hasWarehousePickup = computed(
	() =>
		properties.outlet.content?.warehouse_pickup && currentLang.value === "fi",
);

const distanceText = computed(() => {
	if (!properties.showDistance || !properties.locationCoords) {
		return "";
	}

	const location = properties.outlet.content?.location?.split(",");
	if (!location || location.length < 2) return "0";

	const lat = Number(location[0]) || 0;
	const lng = Number(location[1]) || 0;

	return calculateDistance(
		lat,
		lng,
		properties.locationCoords.latitude,
		properties.locationCoords.longitude,
	);
});
</script>

<i18n lang="json">
{
	"et": {
		"trailer-rental": "Käru rent",
		"warehouse-pickup": "Kättesaamine kohapeal"
	},
	"fi": {
		"trailer-rental": "Peräkärry vuokrattavissa",
		"warehouse-pickup": "Nouda tuotteet varastosta"
	}
}
</i18n>
